import { css, cx } from "@emotion/css";
import { ArrowUpRightFromSquareIcon, PlayIcon } from "@octopusdeploy/design-system-icons";
import { fontSize, fontWeight, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useDispatchLinkAnalytics } from "../../routing/OctopusRoutingContext";
import { formatExternalUrl } from "./formatExternalUrl";
type Size = "xSmall" | "small" | "medium" | "large";
type Weight = "normal" | "bold";
export type BaseLinkProps = {
    href: string;
    children: string;
    external?: boolean;
    video?: boolean;
    openInNewTab?: boolean;
    size?: Size;
    weight?: Weight;
    onClick?: () => void;
    download?: string;
    accessibleName?: string;
};
// For internal use only. Do not export this component.
function BaseLink({ href, children, external = false, video = false, openInNewTab = false, size, weight = "normal", onClick, download, accessibleName }: BaseLinkProps) {
    return (<a className={cx(styles.link, { [styles.bold]: weight === "bold" }, styles.fontSize(size))} href={href} target={openInNewTab ? "_blank" : undefined} rel={openInNewTab ? "noreferrer noopener" : undefined} onClick={onClick} download={download} aria-label={accessibleName}>
            {video ? <PlayIcon size={"font-size"}/> : null}
            <span>{children}</span>
            {external ? <ArrowUpRightFromSquareIcon size={"font-size"}/> : null}
        </a>);
}
export type LinkProps = {
    href: string;
    children: string;
    size?: Size;
    weight?: Weight;
    onClick?: () => void;
};
export function Link({ href, children, size, weight = "normal", onClick }: LinkProps) {
    return (<BaseLink size={size} weight={weight} href={href} onClick={onClick}>
            {children}
        </BaseLink>);
}
type ExternalLinkProps = LinkProps & {
    video?: boolean;
    trackAnalytics?: boolean;
    downloadFileName?: string;
    linkLocation?: string;
    openInSelf?: boolean;
    hideIcon?: boolean;
    accessibleName?: string;
};
export function ExternalLink({ href, children, size, weight, onClick, trackAnalytics = true, video = false, downloadFileName, linkLocation, openInSelf = false, hideIcon = false, accessibleName }: ExternalLinkProps) {
    const dispatchAnalytics = useDispatchLinkAnalytics();
    const handleOnClick = () => {
        if (onClick) {
            onClick();
        }
        if (trackAnalytics === true) {
            dispatchAnalytics(children, href, "Navigate to External Link", linkLocation);
        }
    };
    return (<BaseLink href={formatExternalUrl(href)} size={size} weight={weight} onClick={handleOnClick} external={!hideIcon} openInNewTab={!openInSelf} video={video} download={downloadFileName} accessibleName={accessibleName}>
            {children}
        </BaseLink>);
}
const styles = {
    link: css({
        display: "inline",
        color: themeTokens.color.text.link.default,
        textDecoration: "none",
        "&:hover": {
            color: themeTokens.color.text.link.hover,
        },
        svg: {
            display: "inline",
            verticalAlign: "text-top",
        },
        "svg + span": {
            marginLeft: space[4], // gap between leading icon and label
        },
        "span + svg": {
            marginLeft: space[4], // gap between label and trailing icon
        },
    }),
    bold: css({
        fontWeight: fontWeight[700],
    }),
    fontSize: (size?: Size) => {
        switch (size) {
            case undefined:
                // allow Link to inherit font size from parent
                return undefined;
            case "xSmall":
                return css({
                    fontSize: fontSize.xSmall,
                });
            case "small":
                return css({
                    fontSize: fontSize.small,
                });
            case "medium":
                return css({
                    fontSize: fontSize.medium,
                });
            case "large":
                return css({
                    fontSize: fontSize.base,
                });
        }
    },
};
